/**
 * Alta y edición de lote.
 *
 * El vencimiento es obligatorio en el alta aunque la columna admita NULL: el
 * lote es la identidad GS1 del producto en el mercado —`(01)` GTIN, `(10)`
 * lote, `(17)` vencimiento— y uno sin vencimiento no cumple GS1 ni sirve para
 * el retiro por vencimiento. Es la misma regla que aplica `BatchIn`.
 *
 * En edición el producto no se cambia: `BatchPatch` no lo acepta. Mover un
 * lote de producto reescribiría la identidad de unidades ya activadas.
 */
import { useState, type FormEvent, type ReactNode } from 'react';

import type { Schemas } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { Button } from '@/ui/Button';
import { ErrorState } from '@/ui/states';
import { SelectField, TextField } from '../products/ui/fields';
import { BATCH_STATUS_LABEL_KEYS, EDITABLE_BATCH_STATUSES, isBatchStatus, type BatchStatus } from './status';

type Batch = Schemas['BatchOut'];
type Product = Schemas['ProductOut'];

export interface BatchDraft {
  product_id: number;
  lot_code: string;
  expiry_date: string;
  territory: string | null;
  status: BatchStatus;
}

export interface BatchFormProps {
  /** `undefined` = alta. */
  batch?: Batch;
  /** Catálogo para el desplegable de producto. Vacío en edición. */
  products: readonly Product[];
  /** Producto preseleccionado en el alta (viene del filtro de la lista). */
  defaultProductId?: number | null;
  onSubmit: (draft: BatchDraft) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  error: unknown;
}

export function BatchForm({
  batch,
  products,
  defaultProductId,
  onSubmit,
  onCancel,
  isSubmitting,
  error,
}: BatchFormProps): ReactNode {
  const { t } = useI18n();
  const editing = batch !== undefined;

  const [productId, setProductId] = useState(
    String(batch?.product_id ?? defaultProductId ?? products[0]?.id ?? ''),
  );
  const [lotCode, setLotCode] = useState(batch?.lot_code ?? '');
  const [expiry, setExpiry] = useState(batch?.expiry_date?.slice(0, 10) ?? '');
  const [territory, setTerritory] = useState(batch?.territory ?? '');
  const [status, setStatus] = useState<BatchStatus>(
    batch !== undefined && isBatchStatus(batch.status) && batch.status !== 'recalled'
      ? batch.status
      : 'open',
  );
  const [touched, setTouched] = useState(false);

  const numericProduct = Number(productId);
  const productError =
    touched && !(Number.isInteger(numericProduct) && numericProduct > 0)
      ? t('form.required')
      : undefined;
  const lotError = touched && lotCode.trim() === '' ? t('form.required') : undefined;
  const expiryError = touched && expiry === '' ? t('form.required') : undefined;
  const canSubmit =
    Number.isInteger(numericProduct) && numericProduct > 0 && lotCode.trim() !== '' && expiry !== '';

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    onSubmit({
      product_id: numericProduct,
      lot_code: lotCode.trim(),
      expiry_date: expiry,
      territory: territory.trim() === '' ? null : territory.trim().toUpperCase(),
      status,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      {editing ? null : (
        <SelectField
          label={t('batches.field.product')}
          hint={t('batches.field.product.hint')}
          value={productId}
          onChange={setProductId}
          error={productError}
          required
          options={products.map((product) => ({
            value: String(product.id),
            label: `${product.sku} — ${product.name}`,
          }))}
        />
      )}

      <TextField
        label={t('batches.field.lot')}
        hint={t('batches.field.lot.hint')}
        value={lotCode}
        onChange={setLotCode}
        error={lotError}
        required
        mono
        maxLength={32}
        placeholder="L2026-0731"
      />

      <TextField
        label={t('batches.field.expiry')}
        hint={t('batches.field.expiry.hint')}
        type="date"
        value={expiry}
        onChange={setExpiry}
        error={expiryError}
        required
      />

      <TextField
        label={t('batches.field.territory')}
        hint={t('batches.field.territory.hint')}
        value={territory}
        onChange={setTerritory}
        maxLength={32}
        mono
        placeholder="PY"
      />

      <SelectField
        label={t('batches.field.status')}
        hint={t('batches.field.status.hint')}
        value={status}
        onChange={(value) => {
          if (isBatchStatus(value) && value !== 'recalled') setStatus(value);
        }}
        options={EDITABLE_BATCH_STATUSES.map((value) => ({
          value,
          label: t(BATCH_STATUS_LABEL_KEYS[value]),
        }))}
      />

      {Boolean(error) && <ErrorState error={error} />}

      <div className="flex flex-wrap justify-end gap-2 pt-1">
        <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          {t('form.cancel')}
        </Button>
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? t('form.saving') : t('form.save')}
        </Button>
      </div>
    </form>
  );
}
